import CollaboratorPicker from "@/components/CollaboratorPicker/collaboratorPicker.component";
import CreateSubTask from "@/components/CreateTask/createSubTask.component";
import CreateTaskTags from "@/components/CreateTaskTags/createTaskTags.component";
import CustomLoader from "@/components/CustomLoader/customLoader.component";
import ConvertTaskDialog from "@/components/Dialogs/convertTaskDialog.component";
import DueDatePicker from "@/components/DueDatePicker/dueDatePicker.component";
import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import OverlayWrapper from "@/components/OverlayWrapper/overlayWrapper.component";
import AssigneePicker from "@/components/PersonPickerDropdown/assigneePicker.component";
import QuillTextEditor from "@/components/RichTextEditor/QuillTextEditor/quillTextEditor.component";
import { ActivityChange } from "@/components/SingleTaskViewSheet/activityChange.component";
import SingleTag from "@/components/Tags/tag.component";
import TaskStatusButton from "@/components/TaskStatusButton/taskStatusButton.component";
import TaskTimer from "@/components/TaskTimer/taskTimer.component";
import {
  ROUTE_INBOX,
  ROUTE_SEARCH,
  ROUTE_TASKS,
} from "@/constants/routes.constants";
import { useTasks } from "@/hooks/AppContext/tasks.hook";
import { useUser } from "@/hooks/AppContext/user.hook";
import {
  createTask,
  sendCopyTask,
  sendDeleteTasks,
  updateTask,
  updateTaskStatus,
} from "@/models/tasks.model";
import { AppContext } from "@/services/AppContext/appContext";
import { useTheme } from "@/shadcn/components/theme-provider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcn/components/ui/alert-dialog";
import { Button } from "@/shadcn/components/ui/button";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { Textarea } from "@/shadcn/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import {
  ChildTask,
  Collaborator,
  EditedTask,
  FetchTaskParams,
  NewTask,
  NewTaskTag,
  SingleAuditLog,
  SingleAuditLogChange,
} from "@/types";
import {
  capitalize,
  displayChangeFieldName,
  formatDueDate,
  genericFailureToast,
  genericSuccessToast,
} from "@/utils/utils";
import isEqual from "lodash.isequal";
import {
  ArrowLeft,
  ArrowRightFromLine,
  CheckCircle,
  Copy,
  Link,
  Plus,
  Trash,
  User,
} from "lucide-react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import SingleTaskActivity from "./singleTaskActivity.component";

type SingleTaskViewDialogNormalProps = {
  taskID: number | undefined;
  task: EditedTask;
  // auditLogs: SingleAuditLog[];
  collaborators: Collaborator[];
  subTasks: ChildTask[];
  toggleChat: () => void;
  disableClose?: boolean;
  getFullTask: (taskParams: FetchTaskParams) => Promise<void>;
};

const SingleTaskViewSheetNormal: React.FC<SingleTaskViewDialogNormalProps> = ({
  task,
  taskID,
  // auditLogs,
  collaborators,
  subTasks,
  toggleChat,
  disableClose,
  getFullTask,
}) => {
  const { theme } = useTheme();
  const {
    removeTaskFromUserTasks,
    initialGetUserTasks,
    stopLocalTimer,
    getPrecacheTasks,
    forceFetchAll,
  } = useContext(AppContext);
  const { getUserById, getUserProfileImageByID } = useUser();
  const { getTaskById } = useTasks();
  const nav = useNavigate();
  const location = useLocation();
  const controllerRef = useRef(new AbortController());
  // Deleting
  const [sendingDelete, setSendingDelete] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [sendingCopy, setSendingCopy] = useState<boolean>(false);

  // #region Editing States

  // Status
  const [submittingStatus, setSubmittingStatus] = useState<boolean>(false);

  // Tags
  const [editingTags, setEditingTags] = useState<boolean>(false);
  const [editedTags, setEditedTags] = useState<NewTaskTag[]>(task.tags ?? []);
  const [submittingTags, setSubmittingTags] = useState<boolean>(false);
  const tagRef = useRef<HTMLDivElement>(null);

  // Collaborators
  const [editingCollaborators, setEditingCollaborators] =
    useState<boolean>(false);
  const collaboratorRef = useRef<HTMLDivElement>(null);
  const [submittingCollaborators, setSubmittingCollaborators] =
    useState<boolean>(false);
  const [editedCollaborators, setEditedCollaborators] =
    useState<Collaborator[]>(collaborators);

  useEffect(() => {
    if (!isEqual(editedCollaborators, collaborators)) {
      setEditedCollaborators(collaborators);
    }
  }, [collaborators]);

  // Title
  const [editedTitle, setEditedTitle] = useState<string | null>(
    task.title ?? null
  );
  const [submittingTitle, setSubmittingTitle] = useState<boolean>(false);

  // Description
  const [editedDescription, setEditedDescription] = useState<string | null>(
    task.input ?? null
  );
  const [editingDescription, setEditingDescription] = useState<boolean>(false);
  const [submittingDescription, setSubmittingDescription] =
    useState<boolean>(false);
  const quillWrapRef = useRef<HTMLDivElement>(null);

  // Due Date
  const [editedDueDate, setEditedDueDate] = useState<Date | null | undefined>(
    task.due_date ? new Date(task.due_date) : null
  );
  const [submittingDueDate, setSubmittingDueDate] = useState<boolean>(false);

  // Assignee
  const [editedAssignee, setEditedAssignee] = useState<number | null>(
    task.assigned_to ?? null
  );
  const [submittingAssignee, setSubmittingAssignee] = useState<boolean>(false);

  // Subtask
  const subTaskRef = useRef<HTMLDivElement>(null);
  const [creatingSubTask, setCreatingSubTask] = useState<boolean>(false);
  const [submittingSub, setSubmittingSub] = useState<boolean>(false);
  const [newSubTask, setNewSubTask] = useState<NewTask>({
    assigned_to: task.assigned_to,
    title: "",
    due_date: formatDueDate(new Date()),
    input: "",
    collaborators: task.collaborators,
    tags: [],
    parent_id: task.id,
  });

  const allSubmittingStates = [
    submittingTitle,
    submittingDescription,
    submittingDueDate,
    submittingAssignee,
    submittingTags,
    submittingCollaborators,
    submittingSub,
  ];

  // #endregion Editing States

  // #region Handle Task Updates
  // Status Update
  async function handleStatusUpdate(
    incomingTaskID: number,
    status: "complete" | "incomplete",
    subTask: boolean
  ) {
    if (submittingStatus) return;
    console.log("Task ID: ", taskID);

    setSubmittingStatus(true);
    const res = await updateTaskStatus({
      taskID: incomingTaskID,
      status: status,
    });
    setSubmittingStatus(false);
    if (res.rStatus === "success") {
      // If on subtask, get the parent task
      const taskParams: FetchTaskParams = {
        taskID: subTask && taskID ? taskID : incomingTaskID,
        editing: true,
      };
      await getFullTask(taskParams);
      if (status === "complete") {
        console.log("Removing task from user tasks");
        removeTaskFromUserTasks(incomingTaskID);
        await stopLocalTimer(incomingTaskID);
      } else {
        console.log("Updating user tasks");
        initialGetUserTasks({ force: true });
      }
      // update pre-cached tasks
      getPrecacheTasks(false, true);
    } else {
      genericFailureToast("Error updating task");
    }
    console.log(res);
  }

  // Delete Task
  async function deleteTask() {
    setSendingDelete(true);
    setDeleteError(null);
    const res = await sendDeleteTasks({ taskIDs: [task.id] });
    setSendingDelete(false);
    if (res.rStatus === "success") {
      removeTaskFromUserTasks(task.id);
      await forceFetchAll();
      if (window.location.pathname === `${ROUTE_TASKS}/${taskID}`) {
        // navigate to the tasks page without the task ID
        nav(ROUTE_TASKS);
      } else if (window.location.pathname.includes(ROUTE_INBOX)) {
        // reload the inbox page
        window.location.href = ROUTE_INBOX;
      } else if (window.location.pathname.includes(ROUTE_SEARCH)) {
        console.log("Navigating to search page");
        // modify the location.pathname to remove the task ID
        const newPath = location.pathname.replace(`/${task.id}`, "");
        nav({
          pathname: newPath,
          search: location.search,
        });
      }
      return;
    }

    setDeleteError("Failed to delete task");
  }

  // Title and Description
  async function handleTaskFieldChange(
    field: keyof EditedTask,
    newValue: string | null
  ) {
    console.log("Field: ", field);
    if (submittingTitle && field === "title") {
      return;
    }
    if (submittingDescription && field === "input") {
      return;
    }

    // Title cannot be empty
    if (field === "title" && (!newValue || newValue.trim() === "")) {
      genericFailureToast(`${capitalize(field)} cannot be empty!`);
      setEditedTitle(task.title ?? null);
      return;
    }

    // No change, reset to original
    if (newValue === task[field]) {
      if (field === "title") {
        setEditedTitle(task.title ?? null);
      } else if (field === "input") {
        setEditedDescription(task.input ?? "");
      }
      return;
    }

    // No null values
    if (newValue === null) {
      genericFailureToast("Error updating task");
      setEditedTitle(task.title ?? null);
      setEditedDescription(task.input ?? null);
      return;
    }

    const updatedTask: EditedTask = {
      id: task.id,
      status: undefined,
      title: field === "title" ? newValue : undefined,
      due_date: undefined,
      input: field === "input" ? newValue : undefined,
      collaborators: undefined,
      tags: undefined,
      assigned_to: undefined,
      parent_id: undefined,
    };

    // Update task
    const res = await updateTask({ task: updatedTask });

    console.log(res);
    if (res.rStatus === "success") {
      const controller = new AbortController();
      controllerRef.current = controller;
      const signal = controller.signal;
      genericSuccessToast(`Task ${field} updated`);
      await getFullTask({ taskID: task.id, signal: signal, editing: true });
      // Update the user tasks
      initialGetUserTasks({ force: true });
      // update pre-cached tasks
      getPrecacheTasks(false, true);
    } else {
      genericFailureToast(`Error updating task ${field}`);
      if (field === "title") {
        setEditedTitle(task.title ?? null);
      } else if (field === "input") {
        setEditedDescription(task.input ?? null);
      }
    }
    console.log(`New ${field}: `, newValue);
  }

  // Due Date
  async function handleDueDateChange(date: Date | null | undefined) {
    // No change
    if (date === task.due_date) {
      return;
    }

    // Removing date if date is null
    if (!date) {
      const updatedTask: EditedTask = {
        id: task.id,
        status: undefined,
        title: undefined,
        due_date: null,
        input: undefined,
        collaborators: undefined,
        tags: undefined,
        assigned_to: undefined,
        parent_id: undefined,
      };

      // Update task
      setSubmittingDueDate(true);
      const res = await updateTask({ task: updatedTask });
      setSubmittingDueDate(false);

      if (res.rStatus === "success") {
        genericSuccessToast("Task due date removed");
        setEditedDueDate(null);
        // Update the user tasks
        initialGetUserTasks({ force: true });
        // update pre-cached tasks
        getPrecacheTasks(false, true);
      } else {
        genericFailureToast("Error removing task due date");
        setEditedDueDate(task.due_date ? new Date(task.due_date) : null);
      }
      return;
    }

    // Updating date
    const newDate = formatDueDate(date);
    if (!newDate || newDate === task.due_date) {
      return;
    }

    const updatedTask: EditedTask = {
      id: task.id,
      status: undefined,
      title: undefined,
      due_date: newDate,
      input: undefined,
      collaborators: undefined,
      tags: undefined,
      assigned_to: undefined,
      parent_id: undefined,
    };

    // Update task
    setSubmittingDueDate(true);
    const res = await updateTask({ task: updatedTask });
    setSubmittingDueDate(false);

    if (res.rStatus === "success") {
      genericSuccessToast("Task due date updated");
      setEditedDueDate(date);
      await getFullTask({ taskID: task.id, editing: true });
      // Update the user tasks
      initialGetUserTasks({ force: true });
      // update pre-cached tasks
      getPrecacheTasks(false, true);
    } else {
      genericFailureToast("Error updating task due date");
      setEditedDueDate(task.due_date ? new Date(task.due_date) : null);
    }
  }

  // Assignee
  async function handleAssigneeChange(assigneeID: number | null) {
    console.log("Assignee ID: ", assigneeID);
    console.log("Task Assignee: ", task.assigned_to);
    if (!assigneeID || assigneeID === task.assigned_to) {
      return;
    }

    const updatedTask: EditedTask = {
      id: task.id,
      status: undefined,
      title: undefined,
      due_date: undefined,
      input: undefined,
      collaborators: undefined,
      tags: undefined,
      assigned_to: assigneeID,
      parent_id: undefined,
    };

    // Update task
    setSubmittingAssignee(true);
    const res = await updateTask({ task: updatedTask });
    setSubmittingAssignee(false);

    if (res.rStatus === "success") {
      genericSuccessToast("Task assignee updated");
      setEditedAssignee(assigneeID);
      await getFullTask({ taskID: task.id, editing: true });
      // Update the user tasks
      initialGetUserTasks({ force: true });
      // update pre-cached tasks
      getPrecacheTasks(false, true);
    } else {
      genericFailureToast("Error updating task assignee");
      setEditedAssignee(task.assigned_to ?? null);
    }
  }

  // Tags (Not upload)
  function handleTagChange(newTag: NewTaskTag) {
    console.log("New Tag: ", newTag);

    setEditedTags((prev) => {
      if (!prev) return [];

      const tagExists = prev.some(
        (t) => t.tag === newTag.tag && t.category_id === newTag.category_id
      );

      const updatedTags = tagExists
        ? prev.filter(
            (t) =>
              !(t.tag === newTag.tag && t.category_id === newTag.category_id)
          )
        : [...prev, newTag];

      console.log("Updated Tags: ", updatedTags);
      return updatedTags;
    });
  }

  // Tag Update
  const updateTaskTags = useCallback(
    async (editedTags: NewTaskTag[]) => {
      const updatedTask: EditedTask = {
        id: task.id,
        status: undefined,
        title: undefined,
        due_date: undefined,
        input: undefined,
        collaborators: undefined,
        tags: editedTags,
        assigned_to: undefined,
        parent_id: undefined,
      };

      // Update task
      const res = await updateTask({ task: updatedTask });

      if (res.rStatus === "success") {
        genericSuccessToast("Task tags updated");
        await getFullTask({ taskID: task.id, editing: true });
        // Update the user tasks
        initialGetUserTasks({ force: true });
        // update pre-cached tasks
        getPrecacheTasks(false, true);
      } else {
        genericFailureToast("Error updating task tags");
        setEditedTags(task.tags ?? []);
      }
    },
    [task.id, task.tags, getFullTask, initialGetUserTasks, getPrecacheTasks]
  );

  // Collaborators (Not upload)
  function handleCollaboratorChange(collaboratorID: number) {
    setEditedCollaborators((prev) => {
      if (!prev) return [];

      const collaboratorExists = prev.some(
        (collaborator) => collaborator.id === collaboratorID
      );

      const updatedCollaborators = collaboratorExists
        ? prev.filter((collaborator) => collaborator.id !== collaboratorID)
        : [
            ...prev,
            {
              id: collaboratorID,
              full_name: `${getUserById(collaboratorID)?.first_name ?? ""} ${
                getUserById(collaboratorID)?.surname ?? ""
              }`,
              email: getUserById(collaboratorID)?.email ?? "",
            },
          ];

      return updatedCollaborators;
    });
  }

  // Collaborators Update
  const updateTaskCollaborators = useCallback(
    async (editedCollaboratorsIDs: number[]) => {
      console.log("Edited Collaborators: ", editedCollaboratorsIDs);
      const updatedTask: EditedTask = {
        id: task.id,
        status: undefined,
        title: undefined,
        due_date: undefined,
        input: undefined,
        collaborators: editedCollaboratorsIDs,
        tags: undefined,
        assigned_to: undefined,
        parent_id: undefined,
      };

      // Update task
      const res = await updateTask({ task: updatedTask });

      if (res.rStatus === "success") {
        genericSuccessToast("Task collaborators updated");
        await getFullTask({ taskID: task.id, editing: true });
        // Update the user tasks
        initialGetUserTasks({ force: true });
        // update pre-cached tasks
        getPrecacheTasks(false, true);
      } else {
        genericFailureToast("Error updating task collaborators");
        setEditedCollaborators(collaborators);
      }
    },
    [task.id, collaborators, getFullTask]
  );

  const clearSubTaskState = useCallback(() => {
    setNewSubTask({
      assigned_to: task.assigned_to,
      title: "",
      due_date: formatDueDate(new Date()),
      input: "",
      collaborators: task.collaborators,
      tags: [],
      parent_id: task.id,
    });
    setCreatingSubTask(false);
  }, [task.assigned_to, task.collaborators, task.id]);

  // #endregion Handle Task Updates

  // #region Lifecycle
  // For updates that aren't text inputs, update check on click outside
  // Create Sub Tasks, adding collaborators, adding tags
  useEffect(() => {
    async function handleClickOutside(event: MouseEvent) {
      // #region Tab Submit
      if (
        tagRef.current &&
        !tagRef.current.contains(event.target as Node) &&
        editingTags &&
        !submittingTags
      ) {
        // Sort the tags
        if (editedTags && task.tags) {
          const editedTagsSorted = [...editedTags].sort((a, b) => {
            if (a.category_id !== b.category_id) {
              return a.category_id - b.category_id;
            }
            return a.tag.localeCompare(b.tag);
          });

          const taskTagsSorted = [...task.tags].sort((a, b) => {
            if (a.category_id !== b.category_id) {
              return a.category_id - b.category_id;
            }
            return a.tag.localeCompare(b.tag);
          });
          // Only update if different
          if (!isEqual(editedTagsSorted, taskTagsSorted)) {
            setEditingTags(false);
            setSubmittingTags(true);
            await updateTaskTags(editedTagsSorted);
            setSubmittingTags(false);
          } else {
            setEditedTags(task.tags);
          }
        } else {
          setEditingTags(false);
        }

        setEditingTags(false);
      }
      // #endregion Tab Submit

      // #region Collaborators Submit
      if (
        collaboratorRef.current &&
        !collaboratorRef.current.contains(event.target as Node) &&
        editingCollaborators
      ) {
        console.log("in here");
        const editCollaboratorIDs = editedCollaborators
          .map((collaborator) => collaborator.id)
          .sort((a, b) => a - b);
        const taskCollaboratorIDs = collaborators
          .map((collaborator) => collaborator.id)
          .sort((a, b) => a - b);

        // handled by the backend
        // if (!hasProjectManager(editCollaboratorIDs)) {
        //   genericFailureToast("a Project Manager is required");
        //   setEditingCollaborators(false);
        //   setEditedCollaborators(collaborators);
        //   return;
        // }

        // Only update if different
        if (
          !isEqual(editCollaboratorIDs, taskCollaboratorIDs) &&
          !submittingCollaborators
        ) {
          // Update collaborators
          console.log("Iun here");
          setEditingCollaborators(false);
          setSubmittingCollaborators(true);
          await updateTaskCollaborators(editCollaboratorIDs);
          setSubmittingCollaborators(false);
        } else {
          console.log("in here");
          setEditingCollaborators(false);
          setEditedCollaborators(collaborators);
        }
      }
      // #endregion Collaborators Submit

      // #region Subtask Submit
      if (
        subTaskRef.current &&
        !subTaskRef.current.contains(event.target as Node) &&
        creatingSubTask &&
        !submittingSub
      ) {
        // If the title is empty, don't create the subtask
        if (newSubTask.title === "") {
          clearSubTaskState();
          return;
        }
        setSubmittingSub(true);
        const response = await createTask(newSubTask);
        if (response.rStatus === "success") {
          genericSuccessToast("Subtask created!");
          clearSubTaskState();
          const controller = new AbortController();
          controllerRef.current = controller;
          const signal = controller.signal;
          await getFullTask({ taskID: task.id, signal: signal, editing: true });
          await initialGetUserTasks({ force: true });
          await getPrecacheTasks(false, true);
        } else {
          genericFailureToast("Error creating subtask");
          clearSubTaskState();
        }
        setSubmittingSub(false);
      }

      // #endregion Subtask Submit

      // #region Description Submit (QuillTextEditor)
      if (
        quillWrapRef.current &&
        !quillWrapRef.current.contains(event.target as Node) &&
        editedDescription !== null &&
        !submittingDescription &&
        editingDescription
      ) {
        setSubmittingDescription(true);
        await handleTaskFieldChange("input", editedDescription);
        setSubmittingDescription(false);
      }
      // #endregion Description Submit
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    editedTags,
    editedCollaborators,
    task,
    editingTags,
    editingCollaborators,
    collaborators,
    newSubTask,
    subTaskRef,
    creatingSubTask,
    submittingSub,
    updateTaskTags,
    updateTaskCollaborators,
    clearSubTaskState,
    getFullTask,
    submittingTags,
    submittingCollaborators,
    quillWrapRef,
    editedDescription,
    submittingDescription,
    editingDescription,
  ]);

  // useEffect(() => {
  //   console.log("Task: ", task);
  // }, [task]);

  // #endregion Lifecycle

  // useEffect(() => {
  //   console.log(editingTags);
  //   console.log(editedTags);
  // }, [editingTags, editedTags]);

  function closeTaskView(openParent: boolean) {
    // get the current path
    const currentPath = location.pathname;

    // remove taskID from the path
    const newPath = currentPath.replace(`/${task.id}`, "");

    // get the search params
    const searchParams = new URLSearchParams(location.search);

    //  add the search params to the new path
    const newSearch = searchParams.toString();

    // create the full path
    const fullPath = !openParent
      ? `${newPath}?${newSearch}`
      : `${newPath}/${task.parent_id}?${newSearch}`;

    // navigate to the new path
    nav(fullPath);
  }

  async function copyTaskToNewTask() {
    setSendingCopy(true);
    const res = await sendCopyTask({
      taskID: task.id,
    });

    if (res.rStatus === "success") {
      genericSuccessToast("Task copied to new task!");
      await forceFetchAll();
      nav(`${ROUTE_TASKS}/${res.taskID}`);
    } else {
      genericFailureToast("Error copying task");
    }
    setSendingCopy(false);
  }

  function copyTaskLinkToClipboard() {
    try {
      const taskLink = `${window.location.origin}${ROUTE_TASKS}/${task.id}`;
      navigator.clipboard.writeText(taskLink).then(() => {
        genericSuccessToast("Task link copied to clipboard!");
      });
    } catch (error) {
      console.error("Failed to copy task link:", error);
      genericFailureToast("Failed to copy task link");
    }
  }

  if (!taskID) {
    return (
      <div className="text-center font-bold text-red-600 text-sm m-2">
        Task ID is undefined
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-2 flex-wrap">
        <ErrorBoundary
          fallback={
            <div className="text-center font-bold text-red-600 text-sm m-2">
              Error with task header
            </div>
          }
        >
          <div className="my-2">
            {/* Mark (in)complete */}
            <TaskStatusButton
              taskID={task.id}
              status={task.status}
              handleStatusUpdate={handleStatusUpdate}
            />
          </div>
          <div>
            {sendingDelete && (
              <div className="flex gap-2">
                <CustomLoader size="xs" />
                <div className="text-red-500">Deleting task...</div>
              </div>
            )}
            {
              // delete error
              deleteError && <div className="text-red-500">{deleteError}</div>
            }
          </div>
          {sendingCopy && (
            <div className="flex gap-2">
              <CustomLoader size="xs" />
              <div className="text-red-500">Copying task...</div>
            </div>
          )}
          {allSubmittingStates.some((state) => state) && (
            <div className="flex gap-2">
              <CustomLoader size="xs" />
              <div className="text-red-500">Saving...</div>
            </div>
          )}
          <div className="flex flex-wrap">
            <TooltipProvider>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="btn btn-primary rounded-sm"
                    onClick={copyTaskLinkToClipboard}
                  >
                    <Link />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="">Copy Task Link</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* <TooltipProvider>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="btn btn-primary rounded-sm"
                    onClick={toggleChat}
                  >
                    <ChatBubbleIcon className="w-5 h-5 p-0" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="">Chat With Task</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider> */}

            <AlertDialog>
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipContent>
                    <div>Duplicate Task</div>
                  </TooltipContent>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="btn btn-primary rounded-sm"
                        disabled={sendingDelete || sendingCopy}
                      >
                        <Copy />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Copy this task?</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      await copyTaskToNewTask();
                    }}
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <ConvertTaskDialog
              taskID={taskID}
              subTask={task.parent_id !== null && task.parent_id !== undefined}
            />
            <AlertDialog>
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipContent>
                    <div className="">Delete Task</div>
                  </TooltipContent>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="btn btn-primary rounded-sm"
                        disabled={sendingDelete}
                      >
                        <Trash />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the task.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      await deleteTask();
                    }}
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {!disableClose && (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn btn-primary rounded-sm"
                      onClick={() => {
                        closeTaskView(false);
                      }}
                    >
                      <ArrowRightFromLine className="w-5 h-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="">Close</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </ErrorBoundary>
      </div>
      <ErrorBoundary
        fallback={
          <div className="text-center font-bold text-red-600 text-sm m-2">
            Error with sub task header
          </div>
        }
      >
        {task.parent_id && (
          <Button
            variant="secondary"
            size="sm"
            className="btn btn-primary rounded-sm mb-2 text-wrap truncate"
            onClick={() => {
              closeTaskView(true);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-1 min-w-4" />
            {getTaskById(task.parent_id)?.title}
          </Button>
        )}
      </ErrorBoundary>
      <div className="w-full overflow-hidden">
        <ErrorBoundary
          fallback={
            <div className="text-center font-bold text-red-600 text-sm m-2">
              Error with task title
            </div>
          }
        >
          {/* Title */}
          <Textarea
            className="text-xl mb-3 font-bold resize-none max-h-24"
            value={editedTitle ?? ""}
            onChange={(e) => setEditedTitle(e.target.value)}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto"; // Reset height to auto
              target.style.height = `${target.scrollHeight}px`; // Set height to scrollHeight
            }}
            onBlur={async () => {
              setSubmittingTitle(true);
              await handleTaskFieldChange("title", editedTitle);
              setSubmittingTitle(false);
            }}
            disabled={submittingTitle}
            maxLength={255}
          />
        </ErrorBoundary>
        <div className="grid grid-cols-[1fr_2fr] gap-2 items-center">
          <ErrorBoundary
            fallback={
              <div className="text-center font-bold text-red-600 text-sm m-2">
                Error with task timer
              </div>
            }
          >
            <label className="justify-self-start mr-4" htmlFor="task-timer">
              Timer:
            </label>
            <TaskTimer
              type={{
                location: "task",
                taskName: task.title ?? "",
                taskID: task.id,
              }}
            />
          </ErrorBoundary>
          <ErrorBoundary
            fallback={
              <div className="text-center font-bold text-red-600 text-sm m-2">
                Error with task assignee
              </div>
            }
          >
            <label className="justify-self-start mr-4" htmlFor="task-assignee">
              Assignee:
            </label>
            <OverlayWrapper isLoading={submittingAssignee} className="w-full">
              <AssigneePicker
                assignedTo={editedAssignee}
                setAssignedTo={setEditedAssignee}
                handleFieldAssignedTo={handleAssigneeChange}
                modal={true}
                disabled={submittingAssignee}
              />
            </OverlayWrapper>
          </ErrorBoundary>
          <ErrorBoundary
            fallback={
              <div className="text-center font-bold text-red-600 text-sm m-2">
                Error with task due date
              </div>
            }
          >
            <label className="justify-self-start mr-4" htmlFor="task-due-date">
              Due Date:
            </label>

            <OverlayWrapper
              isLoading={submittingDueDate}
              className="font-bold !mt-2"
            >
              <DueDatePicker
                dueDate={editedDueDate}
                onDateChange={handleDueDateChange}
                setDueDate={setEditedDueDate}
                modal={true}
                disabled={submittingDueDate}
              />
            </OverlayWrapper>
          </ErrorBoundary>
          <ErrorBoundary
            fallback={
              <div className="text-center font-bold text-red-600 text-sm m-2">
                Error with task tags
              </div>
            }
          >
            <label className="justify-self-start mr-4" htmlFor="task-tags">
              Tags:
            </label>
            <OverlayWrapper
              isLoading={submittingTags}
              className="flex flex-wrap gap-2 overflow-hidden"
              onClick={() => {
                setEditingTags(true);
              }}
              divRef={tagRef}
            >
              {(editingTags || editedTags.length === 0) && !submittingTags ? (
                <CreateTaskTags
                  id="edit-tags-side"
                  taskTags={editedTags}
                  handleFieldTags={handleTagChange}
                  side="bottom"
                  createTagAllowed={true}
                />
              ) : (
                editedTags.map((tag, index) => (
                  <SingleTag
                    key={index}
                    id={tag.category_id + tag.tag}
                    name={tag.tag}
                    color={tag.color}
                    full={true}
                    className="truncate max-w-full"
                  />
                ))
              )}
            </OverlayWrapper>
          </ErrorBoundary>
        </div>
        {/* Description + Activity */}
        <div className="w-full overflow-hidden flex flex-col">
          <ErrorBoundary
            fallback={
              <div className="text-center font-bold text-red-600 text-sm m-2">
                Error with task description
              </div>
            }
          >
            <div
              ref={quillWrapRef}
              className="mt-3 h-full w-full"
              onClick={() => {
                setEditingDescription(true);
              }}
            >
              <QuillTextEditor
                key={`description-${task.id}`}
                id={`description-${task.id}`}
                value={editedDescription ?? ""}
                onChange={setEditedDescription}
                placeholder="No description yet"
                readOnly={submittingDescription}
                containerClassName="w-full h-full"
                className="w-full"
              />
            </div>
          </ErrorBoundary>
          <ErrorBoundary
            fallback={
              <div className="text-center font-bold text-red-600 text-sm m-2">
                Error with task activity
              </div>
            }
          >
            <SingleTaskActivity taskID={task.id} />
          </ErrorBoundary>
        </div>
        {/* Subtasks */}
        <div>
          <div className="flex items-center mt-3">
            <h4 className="font-bold align-middle">Subtasks</h4>
            {taskID && (
              <Button
                variant={theme === "light" ? "secondary" : "default"}
                className={`rounded-full items-center justify-center align-middle ml-2 !p-0.5 h-auto leading-none`}
                onClick={() => {
                  setCreatingSubTask(!creatingSubTask);
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
          {/* New Subtask */}
          {creatingSubTask && (
            <div className="mt-3 mb-1 rounded" ref={subTaskRef}>
              <ErrorBoundary
                fallback={
                  <div className="text-center font-bold text-red-600 text-sm m-2">
                    Error with subtask creation
                  </div>
                }
              >
                <CreateSubTask
                  newSubTask={newSubTask}
                  setNewSubTask={setNewSubTask}
                />
              </ErrorBoundary>
            </div>
          )}
          {/* Subtasks */}
          {subTasks.length > 0 && (
            <div className="flex flex-col">
              {subTasks.map((childTask) => (
                <div
                  key={`${task.id}-${childTask.id}`}
                  className={`bg-neutral-300 dark:bg-neutral-900 my-1 rounded text-sm flex flex-row cursor-pointer p-2 justify-between items-center ${
                    childTask.status === "complete" ? "opacity-30" : ""
                  }`}
                  onClick={() => {
                    nav(`${ROUTE_TASKS}/${childTask.id}`);
                  }}
                >
                  <ErrorBoundary
                    fallback={
                      <div className="text-center font-bold text-red-600 text-sm m-2">
                        Error with subtask
                      </div>
                    }
                  >
                    <div className="font-semibold truncate flex-1">
                      {childTask.title}
                    </div>
                    <div className="flex-1 ms-1">
                      <div className="flex gap-1 items-center">
                        {getUserProfileImageByID(childTask.assignee_id) ? (
                          <img
                            className="size-4 rounded-full bg-primary shrink-0"
                            src={getUserProfileImageByID(childTask.assignee_id)}
                            alt={childTask.assignee_name}
                          />
                        ) : (
                          <User className="size-4 rounded-full bg-primary shrink-0" />
                        )}
                        {childTask.assignee_name}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {childTask.due_date
                        ? formatDueDate(childTask.due_date)
                        : "No Due Date"}
                    </div>
                    {/* status */}
                    <div
                      className="check-circle"
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const newStatus =
                          childTask.status === "complete"
                            ? "incomplete"
                            : "complete";
                        await handleStatusUpdate(childTask.id, newStatus, true);
                      }}
                    >
                      <CheckCircle
                        className={`w-5 h-5 ms-3 ${
                          childTask.status === "complete"
                            ? "text-green-500"
                            : "text-gray-700"
                        } md:filter-none`}
                      />
                    </div>
                  </ErrorBoundary>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Collaborators */}
        <div className="mt-3">
          <h4 className="font-bold align-middle">Collaborators</h4>
          <OverlayWrapper
            className="flex flex-row gap-1 flex-wrap overflow-y-auto max-h-20"
            onClick={() => {
              setEditingCollaborators(true);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            divRef={collaboratorRef}
            isLoading={submittingCollaborators}
          >
            {editingCollaborators && !submittingCollaborators ? (
              <div
                className="w-full"
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
              >
                <ErrorBoundary
                  fallback={
                    <div className="text-center font-bold text-red-600 text-sm m-2">
                      Error with task collaborators
                    </div>
                  }
                >
                  <CollaboratorPicker
                    collaborators={editedCollaborators}
                    handleFieldCollaborators={handleCollaboratorChange}
                    collaboratorRef={collaboratorRef}
                  />
                </ErrorBoundary>
              </div>
            ) : (
              editedCollaborators.map((collaborator) => (
                <div
                  key={collaborator.id}
                  className="bg-neutral-300 dark:bg-neutral-900 my-1 rounded text-sm flex flex-row px-2 py-1 justify-between items-center cursor-pointer"
                >
                  <div className="font-bold flex gap-2 items-center">
                    {getUserProfileImageByID(collaborator.id) ? (
                      <img
                        className="size-4 rounded-full bg-primary shrink-0"
                        src={getUserProfileImageByID(collaborator.id)}
                        alt={collaborator.full_name}
                      />
                    ) : (
                      <User className="size-4 rounded-full bg-primary shrink-0" />
                    )}
                    <div>{collaborator.full_name}</div>
                  </div>
                </div>
              ))
            )}
          </OverlayWrapper>
        </div>
      </div>
    </>
  );
};

export default SingleTaskViewSheetNormal;
